import React, { useState, useEffect } from 'react'
import { ReactComponent as IconUser } from 'images/user.svg';
import Button from 'components/shared/Button';
import { Link } from 'react-router-dom';
import useUserIsFollowedByMe from 'sdk/hooks/follow/useUserIsFollowedByMe';
import useRemoveMyFollower from 'sdk/hooks/follow/useRemoveMyFollower';
import useFollowUser from 'sdk/hooks/follow/useFollowUser';

import { useTypedSelector } from 'store/useTypedSelector';
import useGetUsernameById from 'sdk/hooks/profile/useGetUsernameById';
import useUser from 'sdk/hooks/user/useProfile';
import styles from './Follower.module.scss'
import { NoInternetModal } from '../NoInternetModal/NoInternetModal';

type Props = {
  follower: {
    profile?: {
      avatar: string
      first_name: string
      last_name: string
      username: string
    }
    user_id: string
    follow_user_id: string
  }
  profileId?: string
  isMyProfile?: boolean
  refetchFollowers?: () => void
  mode: 'followers' | 'following'
  setCounts: React.Dispatch<React.SetStateAction<{
    followers: number | null;
    following: number | null;
    assets: number | null;
  } | undefined>>
  setSignUpVisible?: () => void
}

export default function Follower({
  follower,
  profileId,
  isMyProfile,
  refetchFollowers,
  mode,
  setCounts,
  setSignUpVisible,
}: Props) {
  const id = mode === 'followers' ? follower.user_id : follower.follow_user_id
  const { data: username } = useGetUsernameById(mode === 'followers' ? follower.user_id : follower.follow_user_id)
  const {
    data: isFollowedByMe,
    refetch: refetchIsFollowedByMe,
    isFetching,
  } = useUserIsFollowedByMe(id)
  const followToUser = useFollowUser(id)
  const removeMyFollower = useRemoveMyFollower(id)
  const isOwnProfile = (id === profileId)
  const { isBecomeOffline } = useTypedSelector((state) => state.postSlice)
  const [isOpenInternetModal, setIsOpenInternetModal] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [isFollow, setIsFollow] = useState(!isOwnProfile && !isFollowedByMe)
  const [isFollowing, setIsFollowing] = useState(isFollowedByMe)
  const { data: currentUser } = useUser()

  useEffect(() => {
    setIsFollow(!isOwnProfile && !isFollowedByMe)
  }, [!isOwnProfile && !isFollowedByMe])

  useEffect(() => {
    setIsFollowing(isFollowedByMe)
  }, [isFollowedByMe])

  const onFollow = async () => {
    if (isBecomeOffline) {
      setIsOpenInternetModal(true)
      return
    }

    if (setSignUpVisible) {
      if (!currentUser) {
        setSignUpVisible()
        return
      }
    }

    setCounts((v) => {
      if (v?.following) {
        return { ...v, following: v.following + 1 }
      }
      return v
    })
    setIsFollowing(true)
    setIsFollow(false)
    followToUser.mutate(true, {
      onSuccess() {
        refetchIsFollowedByMe()
        if (refetchFollowers) refetchFollowers()
      },
    })
  }

  const onUnFollow = async () => {
    if (isBecomeOffline) {
      setIsOpenInternetModal(true)
      return
    }

    if (setSignUpVisible) {
      if (!currentUser) {
        setSignUpVisible()
        return
      }
    }

    setCounts((v) => {
      if (v?.following) {
        return { ...v, following: v.following - 1 }
      }
      return v
    })
    setIsFollowing(false)
    setIsFollow(true)
    if (mode === 'following') {
      setIsVisible(false)
    }
    followToUser.mutate(false, {
      onSuccess() {
        refetchIsFollowedByMe()
        if (refetchFollowers) refetchFollowers()
      },
    })
  }

  const onRemoveMyFollower = async () => {
    if (isBecomeOffline) {
      setIsOpenInternetModal(true)
      return
    }
    setCounts((v) => {
      if (v?.followers) {
        return { ...v, followers: v.followers - 1 }
      }
      return v
    })
    setIsVisible(false)
    removeMyFollower.mutate(false, {
      onSuccess() {
        refetchIsFollowedByMe()
        if (refetchFollowers) refetchFollowers()
      },
    })
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className={styles.userRow}>
      <Link
        to={`/profile/${username}`}
        className={styles.avatarContainer}
      >
        {follower.profile?.avatar ? (
          <img src={follower.profile?.avatar} alt={follower.profile?.username} />
        ) : (
          <IconUser className={styles.avatarDefault} />
        )}
      </Link>
      <div className={styles.usernameCol}>
        <Link
          to={`/profile/${username}`}
          className={styles.fullName}
        >
          {follower.profile?.first_name}
          {' '}
          {follower.profile?.last_name}
        </Link>
        <Link
          to={`/profile/${username}`}
          className={styles.username}
        >
          @
          {follower.profile?.username}
        </Link>
      </div>
      <div className={styles.followBtns}>
        {
          isMyProfile && !isOwnProfile
          && (
            <Button
              onClick={onRemoveMyFollower}
              disabled={removeMyFollower.isLoading || isFetching}
              skeleton
              small
            >
              Remove
            </Button>
          )
        }
        {isFollowing && isMyProfile && (
          <Button
            onClick={onUnFollow}
            skeleton
            small
          >
            Following
          </Button>
        )}
        {isFollow && isMyProfile && (
          <Button
            onClick={onFollow}
            skeleton
            small
          >
            Follow
          </Button>
        )}
      </div>
      <NoInternetModal
        onClose={() => setIsOpenInternetModal(false)}
        isOpen={isOpenInternetModal}
      />
    </div>
  )
}
